/**
 * Shared in-app inbox state: unread count, recent items, mark-as-read.
 * Polls every 60s while the tab is visible; backs off after repeated failures.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

export interface InboxItem {
  id: number;
  eventKey: string;
  entityType: string | null;
  entityId: number | null;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

interface InboxResponse {
  items: InboxItem[];
  unreadCount: number;
  nextCursor: number | null;
}

interface NotificationsContextValue {
  unreadCount: number;
  items: InboxItem[];
  loading: boolean;
  fetchInbox: (opts?: { unreadOnly?: boolean; cursor?: number | null }) => Promise<InboxResponse>;
  refreshUnread: () => Promise<void>;
  markAllRead: () => Promise<void>;
  markRead: (ids: number[]) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

const POLL_MS = 60_000;
const MAX_FAILURES = 3;

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(false);
  const failuresRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const refreshUnread = useCallback(async () => {
    if (!isAuthenticated) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const { data } = await api.get('/api/notifications/inbox/unread-count', {
        signal: controller.signal,
      });
      setUnreadCount(Number(data.unreadCount) || 0);
      failuresRef.current = 0;
    } catch (err) {
      if ((err as { name?: string }).name === 'CanceledError') return;
      failuresRef.current += 1;
    }
  }, [isAuthenticated]);

  const fetchInbox = useCallback(
    async (opts?: { unreadOnly?: boolean; cursor?: number | null }): Promise<InboxResponse> => {
      const empty: InboxResponse = { items: [], unreadCount: 0, nextCursor: null };
      if (!isAuthenticated) return empty;
      setLoading(true);
      try {
        const { data } = await api.get('/api/notifications/inbox', {
          params: {
            limit: 20,
            unreadOnly: opts?.unreadOnly ? 'true' : undefined,
            cursor: opts?.cursor ?? undefined,
          },
        });
        const payload: InboxResponse = {
          items: Array.isArray(data.items) ? data.items : [],
          unreadCount: Number(data.unreadCount) || 0,
          nextCursor: data.nextCursor ?? null,
        };
        if (!opts?.cursor) {
          setItems(payload.items);
        }
        setUnreadCount(payload.unreadCount);
        failuresRef.current = 0;
        return payload;
      } catch {
        failuresRef.current += 1;
        return empty;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated]
  );

  const markAllRead = useCallback(async () => {
    try {
      await api.put('/api/notifications/inbox/read-all');
      setUnreadCount(0);
      setItems((prev) => prev.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() })));
    } catch {
      // Keep previous count; next poll will reconcile.
    }
  }, []);

  const markRead = useCallback(async (ids: number[]) => {
    if (ids.length === 0) return;
    try {
      const { data } = await api.put('/api/notifications/inbox/read', { ids });
      const next = Number(data.unreadCount);
      if (Number.isFinite(next)) setUnreadCount(next);
      const idSet = new Set(ids);
      const now = new Date().toISOString();
      setItems((prev) =>
        prev.map((item) => (idSet.has(item.id) ? { ...item, readAt: item.readAt || now } : item))
      );
    } catch {
      // Ignore; poll will reconcile.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      setItems([]);
      return;
    }

    void refreshUnread();
    void fetchInbox();

    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (failuresRef.current >= MAX_FAILURES) return;
      void refreshUnread();
    };

    const interval = window.setInterval(tick, POLL_MS);
    const onFocus = () => {
      failuresRef.current = 0;
      void refreshUnread();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshUnread();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      abortRef.current?.abort();
    };
  }, [isAuthenticated, refreshUnread, fetchInbox]);

  return (
    <NotificationsContext.Provider
      value={{
        unreadCount,
        items,
        loading,
        fetchInbox,
        refreshUnread,
        markAllRead,
        markRead,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = (): NotificationsContextValue => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return ctx;
};
